import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, Loader2, UserPlus } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { adminService } from '../services/admin-service';

/**
 * Crear un usuario dentro de una empresa cliente.
 *
 * 🔴 Antes, para darle de alta a alguien del equipo de un cliente había que
 * **entrar como el administrador de ese cliente**. Andaba, pero el usuario quedaba
 * creado a nombre del cliente y sin rastro de que lo hicimos nosotros.
 *
 * **El rol de plataforma no está en la lista, y no es un olvido.** Repartir ese
 * rol desde un formulario es exactamente el agujero que estuvo abierto 175 días:
 * un administrador de empresa se creaba un `PLATFORM_ADMIN` y pasaba a ver a
 * todos los clientes. Para hacer a alguien administrador de plataforma se toca la
 * base a mano, con intención.
 */

interface NuevoUsuarioDialogProps {
  abierto: boolean;
  onClose: () => void;
  /** La empresa donde se crea. Si viene fija, no se puede cambiar. */
  empresa: { id: string; name: string };
  onCreado: () => void;
}

const ROLES = [
  { valor: 'SALESPERSON', etiqueta: 'Vendedor', ayuda: 'Cotiza y ve sus propias cotizaciones' },
  { valor: 'APPROVER', etiqueta: 'Aprobador', ayuda: 'Aprueba o rechaza lo que otros cotizan' },
  {
    valor: 'COMPANY_ADMIN',
    etiqueta: 'Admin de empresa',
    ayuda: 'Administra usuarios y permisos de su empresa',
  },
];

const VACIO = { firstName: '', lastName: '', email: '', password: '', role: 'SALESPERSON' };

export function NuevoUsuarioDialog({
  abierto,
  onClose,
  empresa,
  onCreado,
}: NuevoUsuarioDialogProps) {
  const { toast } = useToast();
  const [f, setF] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);

  const claveCorta = f.password.length > 0 && f.password.length < 8;
  const listo =
    f.firstName.trim() &&
    f.lastName.trim() &&
    /\S+@\S+\.\S+/.test(f.email) &&
    f.password.length >= 8;

  function cerrar() {
    setF(VACIO);
    setGuardando(false);
    onClose();
  }

  async function crear() {
    if (!listo) return;
    setGuardando(true);
    try {
      const { user, warning } = await adminService.createUser({
        tenantId: empresa.id,
        email: f.email.trim(),
        password: f.password,
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        role: f.role,
      });
      toast({
        title: `${user.firstName} ${user.lastName} quedó creado`,
        description: warning ?? `Ya puede entrar a ${empresa.name}.`,
      });
      onCreado();
      cerrar();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'No se pudo crear el usuario',
        description: error?.response?.data?.error ?? 'Ocurrió un error.',
      });
      setGuardando(false);
    }
  }

  const rolElegido = ROLES.find((r) => r.valor === f.role);

  return (
    <Dialog open={abierto} onOpenChange={(a) => !a && cerrar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" aria-hidden="true" />
            Nuevo usuario
          </DialogTitle>
          <DialogDescription>
            Se crea dentro de <strong>{empresa.name}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="u-nombre">Nombre *</Label>
              <Input
                id="u-nombre"
                value={f.firstName}
                onChange={(e) => setF({ ...f, firstName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="u-apellido">Apellido *</Label>
              <Input
                id="u-apellido"
                value={f.lastName}
                onChange={(e) => setF({ ...f, lastName: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="u-mail">Correo *</Label>
            <Input
              id="u-mail"
              type="email"
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              No se puede repetir en toda la plataforma, ni siquiera entre empresas distintas.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="u-rol">Rol *</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger id="u-rol">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.valor} value={r.valor}>
                    {r.etiqueta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {rolElegido && <p className="text-xs text-muted-foreground">{rolElegido.ayuda}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="u-clave">Contraseña *</Label>
            {/* En claro: hay que dictarla. */}
            <Input
              id="u-clave"
              type="text"
              autoComplete="off"
              value={f.password}
              onChange={(e) => setF({ ...f, password: e.target.value })}
              placeholder="Al menos 8 caracteres"
              aria-invalid={claveCorta}
            />
            {claveCorta && (
              <p className="text-xs text-destructive">Tiene que tener al menos 8 caracteres.</p>
            )}
          </div>

          <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <p>
              El sistema <strong>no le manda ningún correo</strong>. La contraseña se la tenés que
              dar vos.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={cerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={crear} disabled={!listo || guardando}>
            {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Crear usuario
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default NuevoUsuarioDialog;
